/**
 * Calculus analysis utilities for function behavior analysis.
 * Provides sign analysis which powers monotonicity, concavity, and sign region detection.
 */

import { findRoots, type NumericalConfig } from './numerical-methods';
import type { Sign, SignedInterval } from './intervals';

/**
 * Analyze where a function is positive, negative, or zero.
 *
 * This is the core algorithm that powers:
 * - **Monotonicity**: Apply to f'(x) → positive = increasing, negative = decreasing
 * - **Concavity**: Apply to f''(x) → positive = concave up, negative = concave down
 * - **Sign regions**: Apply to f(x) → where function is above/below x-axis
 *
 * Algorithm:
 * 1. Find all roots of f in [xMin, xMax] using numerical methods
 * 2. Create intervals between consecutive roots
 * 3. Sample the midpoint of each interval to determine sign
 *
 * @param f - The function to analyze
 * @param xMin - Left bound of the analysis range
 * @param xMax - Right bound of the analysis range
 * @param config - Optional numerical configuration
 * @returns Array of intervals with their signs, sorted by start position
 *
 * @example
 * // Find where f(x) = x² - 4 is positive/negative
 * const intervals = analyzeSign(x => x*x - 4, -5, 5);
 * // Returns: [
 * //   { start: -5, end: -2, sign: 'positive', ... },
 * //   { start: -2, end: 2, sign: 'negative', ... },
 * //   { start: 2, end: 5, sign: 'positive', ... }
 * // ]
 *
 * @example
 * // Find monotonicity of f(x) = x³ - 3x by analyzing f'(x) = 3x² - 3
 * const fPrime = (x: number) => 3*x*x - 3;
 * const monotonicity = analyzeSign(fPrime, -5, 5);
 * // positive intervals = increasing, negative intervals = decreasing
 */
export function analyzeSign(
  f: (x: number) => number,
  xMin: number,
  xMax: number,
  config?: Partial<NumericalConfig>,
): SignedInterval[] {
  // Find all roots (sign change points) in the range
  const roots = findRoots(f, xMin, xMax, config);

  // Create intervals between consecutive boundaries
  const boundaries = [xMin, ...roots, xMax];
  const intervals: SignedInterval[] = [];

  for (let i = 0; i < boundaries.length - 1; i++) {
    const start = boundaries[i];
    const end = boundaries[i + 1];

    // Skip degenerate intervals
    if (start >= end) {
      continue;
    }

    // Sample at midpoint to determine sign
    const midpoint = (start + end) / 2;
    const value = f(midpoint);

    let sign: Sign;
    if (value > 0) {
      sign = 'positive';
    } else if (value < 0) {
      sign = 'negative';
    } else {
      sign = 'zero';
    }

    intervals.push({
      start,
      end,
      // First interval starts at boundary (open at -∞, closed otherwise)
      startInclusive: i === 0 && isFinite(xMin),
      // Last interval ends at boundary (open at +∞, closed otherwise)
      endInclusive: i === boundaries.length - 2 && isFinite(xMax),
      sign,
    });
  }

  return intervals;
}

/**
 * Type alias for monotonicity analysis results.
 * Positive = increasing, Negative = decreasing.
 */
export type MonotonicityInterval = SignedInterval;

/**
 * Type alias for concavity analysis results.
 * Positive = concave up, Negative = concave down.
 */
export type ConcavityInterval = SignedInterval;

/**
 * Find monotonicity intervals by analyzing the first derivative.
 * Convenience wrapper around analyzeSign.
 *
 * @param fPrime - The first derivative function f'(x)
 * @param xMin - Left bound of the analysis range
 * @param xMax - Right bound of the analysis range
 * @param config - Optional numerical configuration
 * @returns Array of intervals where positive = increasing, negative = decreasing
 */
export function findMonotonicityIntervals(
  fPrime: (x: number) => number,
  xMin: number,
  xMax: number,
  config?: Partial<NumericalConfig>,
): MonotonicityInterval[] {
  return analyzeSign(fPrime, xMin, xMax, config);
}

/**
 * Find concavity intervals by analyzing the second derivative.
 * Convenience wrapper around analyzeSign.
 *
 * @param fDoublePrime - The second derivative function f''(x)
 * @param xMin - Left bound of the analysis range
 * @param xMax - Right bound of the analysis range
 * @param config - Optional numerical configuration
 * @returns Array of intervals where positive = concave up, negative = concave down
 */
export function findConcavityIntervals(
  fDoublePrime: (x: number) => number,
  xMin: number,
  xMax: number,
  config?: Partial<NumericalConfig>,
): ConcavityInterval[] {
  return analyzeSign(fDoublePrime, xMin, xMax, config);
}
